import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ThemeProvider } from "./components/theme-provider";
import { DashboardLayout } from "./components/layout/DashboardLayout";
import { InstallPWA } from "./components/pwa/InstallPWA";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminLayout } from "./components/layout/AdminLayout";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

// Lazy Load Pages
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const WorkspaceWelcome = lazy(() => import("./pages/WorkspaceWelcome"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Clientes = lazy(() => import("./pages/Clientes"));
const ClienteDetalhe = lazy(() => import("./pages/ClienteDetalhe"));
const ClientApprovals = lazy(() => import("./pages/ClientApprovals"));
const Demandas = lazy(() => import("./pages/Demandas"));
const Calendario = lazy(() => import("./pages/Calendario"));
const Conteudo = lazy(() => import("./pages/Conteudo"));
const BancoIdeias = lazy(() => import("./pages/BancoIdeias"));
const Arquivos = lazy(() => import("./pages/Arquivos"));
const IAAssistente = lazy(() => import("./pages/IAAssistente"));
const TrafegoPago = lazy(() => import("./pages/TrafegoPago"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const Campanhas = lazy(() => import("./pages/Campanhas"));
const Comunicacao = lazy(() => import("./pages/Comunicacao"));
const Configuracoes = lazy(() => import("./pages/Configuracoes"));
const Integracoes = lazy(() => import("./pages/Integracoes"));
const MinhasTarefas = lazy(() => import("./pages/MinhasTarefas"));
const ClientPortal = lazy(() => import("./pages/portal/ClientPortal"));
const ContentApprovalPortal = lazy(() => import("./pages/portal/ContentApprovalPortal"));
const RelatorioClientePublico = lazy(() => import("./pages/RelatorioClientePublico"));
const Financeiro = lazy(() => import("./pages/Financeiro"));
const CRM = lazy(() => import("./pages/CRM"));
const Adherencia = lazy(() => import("./pages/Adherencia"));
const Timesheet = lazy(() => import("./pages/Timesheet"));
const Formularios = lazy(() => import("./pages/Formularios"));
const FormularioPublico = lazy(() => import("./pages/FormularioPublico"));
const Documentos = lazy(() => import("@/pages/Documentos"));
const DocumentoEditar = lazy(() => import("@/pages/DocumentoEditar"));
const MapasMentais = lazy(() => import("@/pages/MapasMentais"));
const MapaMentalEditar = lazy(() => import("@/pages/MapaMentalEditar"));

// Admin Pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminFeatureFlags = lazy(() => import("./pages/admin/AdminFeatureFlags"));
const AdminSubscriptions = lazy(() => import("./pages/admin/AdminSubscriptions"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30000,
    },
    mutations: {
      retry: false,
    },
  },
});

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <p className="text-muted-foreground">Carregando...</p>
    </div>
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <TooltipProvider>
          <Toaster />
          <BrowserRouter>
            <InstallPWA />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/privacidade" element={<Privacidade />} />
                <Route path="/onboarding" element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                } />
                <Route path="/welcome" element={
                  <ProtectedRoute>
                    <WorkspaceWelcome />
                  </ProtectedRoute>
                } />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/portal/content/:token" element={<ContentApprovalPortal />} />
                <Route path="/portal/:token" element={<ClientPortal />} />
                <Route path="/relatorio/:token" element={<RelatorioClientePublico />} />
                <Route path="/formulario/:token" element={<FormularioPublico />} />

                {/* Admin Routes */}
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminDashboard />} />
                  <Route path="users" element={<AdminUsers />} />
                  <Route path="flags" element={<AdminFeatureFlags />} />
                  <Route path="subscriptions" element={<AdminSubscriptions />} />
                  <Route path="settings" element={<AdminSettings />} />
                </Route>

                <Route element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/minhas-tarefas" element={<MinhasTarefas />} />
                  <Route path="/clientes" element={<Clientes />} />
                  <Route path="/clientes/:id" element={<ClienteDetalhe />} />
                  <Route path="/formularios" element={<Formularios />} />
                  <Route path="/documentos" element={<Documentos />} />
                  <Route path="/documentos/:id" element={<DocumentoEditar />} />
                  <Route path="/mapas-mentais" element={<MapasMentais />} />
                  <Route path="/mapas-mentais/:id" element={<MapaMentalEditar />} />
                  <Route path="/demandas" element={<Demandas />} />
                  <Route path="/calendario" element={<Calendario />} />
                  <Route path="/conteudo" element={<Conteudo />} />
                  <Route path="/aprovacoes" element={<ClientApprovals />} />
                  <Route path="/ideias" element={<BancoIdeias />} />
                  <Route path="/arquivos" element={<Arquivos />} />
                  <Route path="/ia" element={<IAAssistente />} />
                  <Route path="/relatorios" element={<Relatorios />} />
                  <Route path="/timesheet" element={<Timesheet />} />
                  <Route path="/financeiro" element={<Financeiro />} />
                  <Route path="/crm" element={<CRM />} />
                  <Route path="/adherencia" element={<Adherencia />} />
                  <Route path="/trafego" element={<TrafegoPago />} />
                  <Route path="/campanhas" element={<Campanhas />} />
                  <Route path="/comunicacao" element={<Comunicacao />} />
                  <Route path="/configuracoes" element={<Configuracoes />} />
                  <Route path="/integracoes" element={<Integracoes />} />
                  <Route path="/integracoes/callback" element={<Integracoes />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
